/** Top panel filters for DRI insights using FilterStripLayout (same shell as Utilization). */
import React, { useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { FilterRequest, DriDataScope } from '../../../types/api';
import { dashboardApi } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { useFilterOptions } from '../../../contexts/FilterOptionsContext';
import { hasAdminAccess } from '../../../utils/AdminLogic';
import { listContainsSbu, sortBusinessUnits } from '../../../utils/buUtils';
import SelectAllMultiSelect, { FILTER_STANDARD, type SelectAllFlatSection } from '../../common/SelectAllMultiSelect';
import DriProductSectionSelect from './DriProductSectionSelect';
import FilterStripLayout from '../FilterStripLayout';

export type DriFilterUpdater = FilterRequest | ((prev: FilterRequest) => FilterRequest);

interface DriTopFiltersProps {
  filters: FilterRequest;
  onFiltersChange: (next: DriFilterUpdater) => void;
  onRefresh: () => void;
  hasUnsavedChanges?: boolean;
  assignedProducts?: string[];
  optionalProducts?: string[];
  productsLoading?: boolean;
}

/** Renders BU/SBU/Product controls for DRI insights with delivery vs org scope. */
const DriTopFilters: React.FC<DriTopFiltersProps> = ({
  filters,
  onFiltersChange,
  onRefresh,
  hasUnsavedChanges = false,
  assignedProducts = [],
  optionalProducts = [],
  productsLoading = false,
}) => {
  const { user } = useAuth();
  const isAdmin = hasAdminAccess(user);
  const scope: DriDataScope = filters.scope || 'delivery';
  const isOrgScope = scope === 'org';

  const { filterOptions, isLoading: filterOptionsLoading, error: filterOptionsError, refetch: refetchFilterOptions } =
    useFilterOptions();

  const deliveryFilterOptions = filterOptions || { business_units: [], sub_bus: [] };

  const { data: orgBusinessUnits = [], isLoading: orgBuLoading } = useQuery({
    queryKey: ['dri-org-business-units', user?.id],
    queryFn: () => dashboardApi.getDriOrgBusinessUnits(),
    enabled: !!user && isAdmin && isOrgScope,
    staleTime: 15 * 60 * 1000,
  });

  const { data: orgSubBuOptions = [], isLoading: orgSubBuLoading } = useQuery({
    queryKey: ['dri-org-sub-bus', user?.id, filters.business_units],
    queryFn: async () => {
      if (!filters.business_units || filters.business_units.length === 0) return [];
      return dashboardApi.getDriOrgSubBus(filters.business_units);
    },
    enabled: !!user && isAdmin && isOrgScope && !!(filters.business_units && filters.business_units.length > 0),
    staleTime: 5 * 60 * 1000,
  });

  const { data: deliverySubBuOptions, isLoading: deliverySubBuLoading, error: deliverySubBuError } = useQuery({
    queryKey: ['sub-bu-options', user?.id, filters.business_units, 'delivery'],
    queryFn: async () => {
      if (!filters.business_units || filters.business_units.length === 0) return [];
      return await dashboardApi.getSubBusByBusinessUnits(filters.business_units);
    },
    enabled: !!user && !isOrgScope && !!(filters.business_units && filters.business_units.length > 0),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 2,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  const businessUnitOptions = useMemo(() => {
    const list = isOrgScope ? orgBusinessUnits : deliveryFilterOptions.business_units;
    return sortBusinessUnits(list).map((bu) => ({ value: bu, label: bu }));
  }, [isOrgScope, orgBusinessUnits, deliveryFilterOptions.business_units]);

  const availableSubBus = useMemo(() => {
    if (isOrgScope) {
      if (filters.business_units && filters.business_units.length > 0) {
        return orgSubBuOptions;
      }
      return [];
    }
    if (filters.business_units && filters.business_units.length > 0 && deliverySubBuOptions) {
      return deliverySubBuOptions;
    }
    return deliveryFilterOptions.sub_bus || [];
  }, [
    isOrgScope,
    filters.business_units,
    orgSubBuOptions,
    deliverySubBuOptions,
    deliveryFilterOptions.sub_bus,
  ]);

  const buOptionsLoading = isOrgScope ? orgBuLoading : filterOptionsLoading;
  const subBuLoading = isOrgScope ? orgSubBuLoading : deliverySubBuLoading || filterOptionsLoading;

  const finalAvailableProducts = useMemo(() => [...assignedProducts, ...optionalProducts], [assignedProducts, optionalProducts]);
  const sbuOptions = useMemo(() => availableSubBus.map((sbu) => ({ value: sbu, label: sbu })), [availableSubBus]);

  const productFlatSections = useMemo(() => {
    if (isOrgScope) {
      const allItems = assignedProducts.map((p) => ({ value: p, label: p }));
      if (allItems.length === 0) return [];
      return [{ header: 'All Products', items: allItems }];
    }

    const assignedItems = assignedProducts.map((p) => ({ value: p, label: p }));
    const baseOptional = optionalProducts.map((p) => ({ value: p, label: p }));
    const seen = new Set([...assignedProducts, ...optionalProducts]);
    const orphanSelected = (filters.products || []).filter((p) => !seen.has(p));
    const orphanItems = orphanSelected.map((p) => ({ value: p, label: p }));
    const optionalItems = [...baseOptional, ...orphanItems];

    const sections: SelectAllFlatSection[] = [];
    if (assignedItems.length > 0) {
      sections.push({ header: 'Assigned Products', items: assignedItems });
    }
    if (optionalItems.length > 0) {
      sections.push({
        header: 'Optional Products',
        muted: true,
        indentItems: true,
        items: optionalItems,
      });
    }
    return sections;
  }, [isOrgScope, assignedProducts, optionalProducts, filters.products]);

  const canonicalSelectedSubBus = useMemo(
    () => availableSubBus.filter((o) => listContainsSbu(filters.sub_bus || [], o)),
    [availableSubBus, filters.sub_bus]
  );

  const handleScopeChange = (nextScope: DriDataScope) => {
    if (nextScope === 'org') {
      onFiltersChange({
        ...filters,
        scope: 'org',
        business_units: [],
        sub_bus: [],
        products: [],
      });
    } else {
      onFiltersChange({
        ...filters,
        scope: 'delivery',
        business_units: deliveryFilterOptions.business_units || [],
        sub_bus: [],
        products: [],
      });
    }
  };

  const buLoadingMsg = buOptionsLoading ? 'Loading...' : undefined;
  const sbuLoadingMsg = subBuLoading
    ? filters.business_units && filters.business_units.length > 0
      ? 'Loading Sub-BUs...'
      : 'Loading...'
    : undefined;
  const prodLoadingMsg = productsLoading ? 'Loading products...' : undefined;
  const sbuEmptyMsg = isOrgScope
    ? filters.business_units?.length
      ? 'No Sub-BUs for selected BUs'
      : 'All org (no BU filter) or select BUs'
    : filters.business_units?.length
      ? 'No Sub-BUs assigned for selected BUs'
      : 'Select Business Units first';
  const prodEmptyMsg =
    isOrgScope || filters.business_units?.length
      ? 'No products available'
      : 'Select Business Units first';

  const colBase = {
    flex: '1 1 160px',
    minWidth: 112,
    maxWidth: 200,
    display: 'flex',
    alignItems: 'flex-end',
  } as const;
  const colScopeSx = { ...colBase, flex: '0 1 120px', minWidth: 100, maxWidth: 140 } as const;
  const colProductsSx = { ...colBase, flex: '1.1 1 180px', maxWidth: 220 } as const;

  const noBuOptions = !buOptionsLoading && businessUnitOptions.length === 0;

  return (
    <FilterStripLayout
      onClear={() => {
        if (isOrgScope) {
          onFiltersChange({
            scope: 'org',
            business_units: [],
            sub_bus: [],
            products: assignedProducts.length > 0 ? [...assignedProducts] : [],
          });
        } else {
          onFiltersChange({
            scope: 'delivery',
            business_units: deliveryFilterOptions.business_units || [],
            sub_bus: [],
            products: [],
          });
        }
      }}
      onRefresh={onRefresh}
      hasUnsavedChanges={hasUnsavedChanges}
      dense
      alert={
        filterOptionsError && !isOrgScope ? (
          <Alert severity="warning" sx={{ mb: 0 }} action={<Button color="inherit" size="small" onClick={() => refetchFilterOptions()}>Retry</Button>}>
            Failed to load filter options. Click retry to reload.
          </Alert>
        ) : deliverySubBuError && !isOrgScope && filters.business_units?.length ? (
          <Alert severity="warning" sx={{ mb: 0 }}>Failed to load Sub-BU options. Showing all available Sub-BUs.</Alert>
        ) : isOrgScope ? (
          <Alert severity="info" sx={{ mb: 0 }}>
            Org scope: BUs from employees data; all DRI products are selected by default (not limited to bu_to_product mapping).
          </Alert>
        ) : undefined
      }
    >
      {isAdmin && (
        <Box sx={colScopeSx}>
          <FormControl fullWidth size="small" sx={{ minWidth: 0 }}>
            <InputLabel id="dri-scope-label">Scope</InputLabel>
            <Select
              labelId="dri-scope-label"
              label="Scope"
              value={scope}
              onChange={(e) => handleScopeChange(e.target.value as DriDataScope)}
            >
              <MenuItem value="delivery">Delivery</MenuItem>
              <MenuItem value="org">Org</MenuItem>
            </Select>
          </FormControl>
        </Box>
      )}
      <Box sx={colBase}>
        <Box sx={{ width: '100%', minWidth: 0 }}>
          <Tooltip
            title={noBuOptions ? (isOrgScope ? 'No BUs in employees data.' : 'No BUs assigned. Contact admin.') : ''}
            placement="top"
            arrow
          >
            <Box sx={{ width: '100%' }}>
              <SelectAllMultiSelect
                label="Business Unit"
                options={businessUnitOptions}
                selectedValues={filters.business_units || []}
                onChange={(vals) =>
                  onFiltersChange((prev) => ({
                    ...prev,
                    business_units: vals,
                    sub_bus: [],
                    ...(isOrgScope ? {} : { products: [] }),
                  }))
                }
                disabled={buOptionsLoading || noBuOptions}
                loadingMessage={buLoadingMsg}
                emptyMessage={isOrgScope ? 'No business units in employees data' : 'No Business Units assigned to you'}
                renderValue={(selected) => (
                  <Typography sx={{ fontSize: FILTER_STANDARD.fontSize, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}>
                    {buOptionsLoading ? '' : noBuOptions ? 'No BUs' : selected.length === 0 ? (isOrgScope ? 'All org' : '') : `${selected.length} selected`}
                  </Typography>
                )}
              />
            </Box>
          </Tooltip>
        </Box>
      </Box>
      <Box sx={colBase}>
        <Box sx={{ width: '100%', minWidth: 0 }}>
          <Tooltip
            title={
              !subBuLoading && availableSubBus.length === 0 && filters.business_units?.length
                ? 'No Sub-BUs for selected BUs.'
                : ''
            }
            placement="top"
            arrow
          >
            <Box sx={{ width: '100%' }}>
              <SelectAllMultiSelect
                label="Sub-BU"
                options={sbuOptions}
                selectedValues={canonicalSelectedSubBus}
                onChange={(vals) => onFiltersChange((prev) => ({ ...prev, sub_bus: vals }))}
                disabled={
                  subBuLoading ||
                  (availableSubBus.length === 0 && !!(filters.business_units && filters.business_units.length > 0))
                }
                loadingMessage={sbuLoadingMsg}
                emptyMessage={sbuEmptyMsg}
                renderValue={(selected) => {
                  const valid = selected.filter((i) => availableSubBus.includes(i));
                  return (
                    <Typography sx={{ fontSize: FILTER_STANDARD.fontSize, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}>
                      {subBuLoading
                        ? ''
                        : availableSubBus.length === 0 && filters.business_units?.length
                          ? 'No SBUs'
                          : valid.length === 0
                            ? ''
                            : `${valid.length} selected`}
                    </Typography>
                  );
                }}
              />
            </Box>
          </Tooltip>
        </Box>
      </Box>
      <Box sx={colProductsSx}>
        <Box sx={{ width: '100%', minWidth: 0 }}>
          <DriProductSectionSelect
            label="Products"
            flatSections={productFlatSections}
            selectedValues={filters.products || []}
            onChange={(vals) => onFiltersChange((prev) => ({ ...prev, products: vals }))}
            disabled={finalAvailableProducts.length === 0 && !productsLoading && !isOrgScope && !filters.business_units?.length}
            loading={productsLoading}
            loadingMessage={prodLoadingMsg}
            emptyMessage={prodEmptyMsg}
            selectAllValues={assignedProducts}
            renderValue={(selected) => (
              <Typography sx={{ fontSize: FILTER_STANDARD.fontSize, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}>
                {productsLoading
                  ? ''
                  : finalAvailableProducts.length === 0
                    ? isOrgScope
                      ? 'Loading…'
                      : filters.business_units?.length
                        ? 'No products'
                        : 'Select BUs first'
                    : selected.length === 0
                      ? ''
                      : `${selected.length} selected`}
              </Typography>
            )}
          />
        </Box>
      </Box>
    </FilterStripLayout>
  );
};

export default DriTopFilters;
